import { DefaultTheme } from 'styled-components';

import { CsvImportBucketVariant } from '@/features/csvImport/admin/types/csvImport';

export type CsvImportBucketVariantColors = {
  /** Border, icon and dot colour */
  accent: string;
  /** Tinted surface behind a card or chip */
  surface: string;
  /** Colour of the label text sitting on `surface` */
  label: string;
};

/**
 * Single source of the bucket palette, so a summary card, a section chip and its dot can
 * never drift apart.
 */
/**
 * The design tints these two surfaces with the 98 tone, which `theme.tones` cannot express:
 * its tonal palettes step straight from 95 to 99, and adding a stop to `TonalPalette` would
 * mean inventing values for the six palettes this design never specifies. Held here as the
 * literals the design names, `tone/error/98` and `tone/warning/98`.
 */
const ERROR_SURFACE = '#FFF8F7';
const WARNING_SURFACE = '#FFF8F3';

export const getBucketVariantColors = (
  { colors, tones }: DefaultTheme,
  variant: CsvImportBucketVariant,
): CsvImportBucketVariantColors =>
  ({
    error: { accent: colors.error.main, surface: ERROR_SURFACE, label: colors.error.main },
    // The amber accent is the 60 tone rather than `colors.warning.main`, which is the much
    // darker 40 tone and reads as brown against the tinted surface. Only the border, icon and
    // dot carry that amber — the design keeps this variant's text neutral.
    warning: { accent: tones.warning[60], surface: WARNING_SURFACE, label: colors.neutral.onContainer },
    neutral: { accent: colors.neutral.outline2, surface: tones.neutral[99], label: colors.neutral.onContainer },
    info: { accent: colors.tertiary.main, surface: tones.tertiary[95], label: colors.tertiary.main },
  })[variant];
